import React from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight, Coins, Receipt, Banknote } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const FEATURES = [
    {
        Icon: Coins,
        en: { title: "Cash collection", body: "Record cash you've collected from new members on the spot." },
        ar: { title: "تحصيل نقدي",      body: "سجّل المبالغ النقدية المُحصّلة من الأعضاء الجدد فوراً." },
    },
    {
        Icon: Receipt,
        en: { title: "Commission ledger", body: "Track commission per member sign-up and provider onboarding." },
        ar: { title: "سجل العمولات",       body: "تتبع العمولة لكل عضو تم تسجيله ومزود تمت إضافته." },
    },
    {
        Icon: Banknote,
        en: { title: "Finance reconciliation", body: "Hand cash off to finance and get a digital receipt." },
        ar: { title: "تسوية مع المالية",         body: "سلّم النقد للمالية واحصل على إيصال رقمي." },
    },
];

export default function KioskWalletPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    return (
        <div className="max-w-2xl space-y-6 fade-up" data-testid="page-kiosk-wallet">
            <header>
                <p className="text-sm font-medium text-primary">{tr("Sales Kiosk", "كشك المبيعات")}</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight">
                    {tr("My Wallet", "محفظتي")}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    {tr(
                        "Cash collection and commission tracking for sales agents.",
                        "تحصيل نقدي وتتبع عمولات لمندوبي المبيعات."
                    )}
                </p>
            </header>

            {/* Hero */}
            <div className="rounded-2xl border-2 border-amber-200/60 bg-gradient-to-br from-amber-50 to-amber-50/30 p-8 text-center">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-amber-500/10 text-amber-600 mb-3">
                    <Wallet className="h-7 w-7" />
                </div>
                <h2 className="text-xl font-bold text-amber-900">
                    {tr("Coming soon", "قريباً")}
                </h2>
                <p className="mt-1.5 text-sm text-amber-800/80 max-w-md mx-auto">
                    {tr(
                        "We're building the cash wallet and commission ledger as part of the finance module.",
                        "نعمل على المحفظة النقدية وسجل العمولات ضمن وحدة المالية."
                    )}
                </p>
            </div>

            {/* What's coming */}
            <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">
                    {tr("What you'll be able to do", "ما ستتمكن من القيام به")}
                </h3>
                <div className="space-y-2.5">
                    {FEATURES.map(({ Icon, en, ar }, i) => {
                        const txt = lang === "ar" ? ar : en;
                        return (
                            <div key={i} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
                                <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold">{txt.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{txt.body}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Cross-links */}
            <div className="rounded-2xl border border-border bg-muted/20 p-5">
                <p className="text-sm font-semibold mb-2">
                    {tr("In the meantime", "حتى ذلك الحين")}
                </p>
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/kiosk/register"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90"
                    >
                        {tr("Register a member", "تسجيل عضو")}
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <Link
                        to="/kiosk/sales"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80"
                    >
                        {tr("View my sales", "عرض مبيعاتي")}
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
